/**
 * Pronto · Capacitor native bridge
 * ═══════════════════════════════════════════════════════════════════════
 * Wrapper unico per accedere ai plugin nativi quando l'app gira dentro
 * Capacitor (iOS/Android), con fallback al web quando gira sul browser.
 *
 * iter v29 r109 — Setup iOS App Store (Settimana 1).
 * Importa SEMPRE da qui, mai direttamente dai plugin Capacitor:
 *   ✅  import { isNative, nativeCamera } from "@/lib/native"
 *   ❌  import { Camera } from "@capacitor/camera"
 *
 * In questo modo possiamo aggiungere fallback web (es. <input type=file>),
 * loggare le chiamate, o mockare nei test senza toccare 50 componenti.
 */
import { Capacitor } from "@capacitor/core";

/** True quando l'app gira dentro Capacitor (iOS o Android), false su browser. */
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // "ios" | "android" | "web"
export const isIOS = platform === "ios";

/* ─────────────────────────────────────────────────────────────────────
 * GLOBAL HAPTIC — intercetta i click su tutti i bottoni "primary action"
 * dell'app e fa scattare un Taptic Engine feedback leggero senza dover
 * modificare ogni singolo onClick.
 *
 * iter v29 r220 — Installato una volta sola da App.js. Usa event delegation
 * sul document → zero overhead di mount. Pattern di selezione:
 *   • Element con [data-haptic="light|medium|heavy"]  → tipo esplicito
 *   • <button class="btn-primary|btn-cta|btn-action"> → haptic "light"
 * Niente haptic se NON è ambiente nativo (su web/desktop NOOP).
 * ───────────────────────────────────────────────────────────────────── */
let _hapticInstalled = false;
export function installGlobalHaptic() {
  if (!isNative || _hapticInstalled) return;
  _hapticInstalled = true;

  const HAPTIC_CLASSES = ["btn-primary", "btn-cta", "btn-action"];

  document.addEventListener(
    "click",
    (e) => {
      const path = e.composedPath ? e.composedPath() : [];
      for (const node of path) {
        if (!node || node.nodeType !== 1) continue;
        const explicit = node.getAttribute && node.getAttribute("data-haptic");
        if (explicit) {
          nativeHaptic(explicit).catch(() => {});
          return;
        }
        const cls = node.className && typeof node.className === "string" ? node.className : "";
        for (const klass of HAPTIC_CLASSES) {
          if (cls.includes(klass)) {
            nativeHaptic("light").catch(() => {});
            return;
          }
        }
      }
    },
    { capture: true, passive: true }
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * REMOTE LOG — invia eventi diagnostici al backend (/api/client-error)
 * iter v29 r203 — Quando l'app gira su iPhone TestFlight non possiamo
 * vedere la console JS senza un Mac collegato. Per debuggare il flow
 * Google Sign-In nativo, ogni step manda un evento al backend con un
 * tag specifico, così possiamo leggere la sequenza esatta via
 *   GET /api/client-error/recent?tag=ios-google-login
 * Fire-and-forget: NON blocca mai il flow se il backend è giù.
 * ───────────────────────────────────────────────────────────────────── */
export function remoteLog(tag, message, extra) {
  try {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (!apiBase) return;
    const payload = {
      message: String(message).slice(0, 1900),
      stack: extra ? JSON.stringify(extra).slice(0, 7900) : undefined,
      path: typeof window !== "undefined" ? window.location.pathname : undefined,
      ua: typeof navigator !== "undefined" ? `${platform}|${navigator.userAgent}`.slice(0, 290) : platform,
      tag: String(tag).slice(0, 90),
    };
    // Non aspettiamo la risposta: deve essere invisibile per la UX.
    fetch(`${apiBase}/api/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
}

/* ─────────────────────────────────────────────────────────────────────
 * CAMERA — scatto foto sopralluogo + accesso libreria
 * Doc: https://capacitorjs.com/docs/apis/camera
 * iter v32 r27 — User: «nessun controllo zoom 0.5x/1x/2x come iPhone».
 * Soluzione finale: usiamo `@capacitor-community/camera-preview` che dà
 * viewfinder HTML su cui disegniamo controlli Apple-style (zoom presets,
 * flash, switch). Il vecchio `@capacitor/camera` viene tenuto solo come
 * fallback per la libreria foto e per device dove camera-preview fallisce.
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeCameraTake({ quality = 85 } = {}) {
  if (!isNative) return null;
  // Prova prima il nuovo viewfinder custom con zoom 0.5x/1x/2x
  try {
    const { openNativeCameraSheet } = await import("../components/NativeCameraSheet");
    const photo = await openNativeCameraSheet();
    if (photo?.dataUrl) {
      return {
        dataUrl: photo.dataUrl,
        format: photo.format || "jpeg",
        width: undefined,
        height: undefined,
      };
    }
    return null; // utente ha annullato
  } catch (e) {
    // Fallback al vecchio picker se il viewfinder custom fallisce
    // (es. permessi negati lato OS, plugin non installato in vecchie build)
    try {
      const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
      const photo = await Camera.getPhoto({
        quality,
        width: 4032,
        height: 3024,
        preserveAspectRatio: true,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        saveToGallery: false,
        correctOrientation: true,
        promptLabelHeader: "Foto sopralluogo",
        promptLabelCancel: "Annulla",
        promptLabelPhoto: "Da libreria",
        promptLabelPicture: "Scatta foto",
      });
      return {
        dataUrl: photo.dataUrl,
        format: photo.format || "jpeg",
        width: photo.exif?.PixelWidth,
        height: photo.exif?.PixelHeight,
      };
    } catch {
      return null;
    }
  }
}

/**
 * Apri la libreria foto del device (Photos su iOS) senza passare per la camera.
 * Necessario perché il nuovo NativeCameraSheet non gestisce la libreria — usa
 * il picker classico di iOS che è ottimo per quello scopo.
 */
export async function nativePickFromLibrary({ quality = 85 } = {}) {
  if (!isNative) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const photo = await Camera.getPhoto({
      quality,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
      promptLabelHeader: "Scegli foto",
      promptLabelCancel: "Annulla",
    });
    return {
      dataUrl: photo.dataUrl,
      format: photo.format || "jpeg",
      width: photo.exif?.PixelWidth,
      height: photo.exif?.PixelHeight,
    };
  } catch {
    return null;
  }
}

export async function nativeCameraPickFromLibrary({ quality = 85, multiple = false } = {}) {
  if (!isNative) return null;
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  try {
    if (multiple) {
      const result = await Camera.pickImages({ quality, limit: 20 });
      return (result.photos || []).map((p) => ({ webPath: p.webPath, format: p.format }));
    }
    const photo = await Camera.getPhoto({
      quality,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
    });
    return { dataUrl: photo.dataUrl, format: photo.format || "jpeg" };
  } catch (e) {
    return null;
  }
}

/* ─────────────────────────────────────────────────────────────────────
 * SPEECH RECOGNITION — voice command "Parla con Pronto"
 * Doc: https://github.com/capacitor-community/speech-recognition
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeSpeechStart({
  language = "it-IT",
  partial = true,
  onPartial,
  onResult,
  onError,
} = {}) {
  if (!isNative) {
    onError?.(new Error("Non disponibile su browser"));
    return null;
  }
  const { SpeechRecognition } = await import("@capacitor-community/speech-recognition");

  // Check + chiedi permesso se serve
  const avail = await SpeechRecognition.available();
  if (!avail.available) {
    onError?.(new Error("Riconoscimento vocale non disponibile su questo dispositivo"));
    return null;
  }
  const perm = await SpeechRecognition.checkPermissions();
  if (perm.speechRecognition !== "granted") {
    const req = await SpeechRecognition.requestPermissions();
    if (req.speechRecognition !== "granted") {
      onError?.(new Error("Permessi microfono/voce non concessi"));
      return null;
    }
  }

  // Listener parziali (live transcript)
  const partialHandle = await SpeechRecognition.addListener(
    "partialResults",
    (data) => onPartial?.(data?.matches?.[0] || "")
  );
  const finalHandle = await SpeechRecognition.addListener(
    "listeningState",
    (data) => { if (!data?.status || data.status === "stopped") onResult?.(); }
  );

  await SpeechRecognition.start({
    language,
    maxResults: 1,
    partialResults: partial,
    popup: false,
  });

  return {
    stop: async () => {
      try { await SpeechRecognition.stop(); } catch (_) { /* silent */ }
      try { partialHandle.remove(); } catch (_) { /* silent */ }
      try { finalHandle.remove(); } catch (_) { /* silent */ }
    },
  };
}

/* ─────────────────────────────────────────────────────────────────────
 * APPLE SIGN-IN — richiesta Apple per app con altri social login
 * Doc: https://github.com/capacitor-community/apple-sign-in
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeAppleSignIn() {
  if (!isIOS) throw new Error("Sign in with Apple disponibile solo su iOS");
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");

  // Genera nonce casuale per anti-replay (verificato lato backend)
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, "0")).join("");

  const result = await SignInWithApple.authorize({
    clientId: "it.prontoimmo.app",
    redirectURI: "https://prontoimmo.it/auth/apple/callback",
    scopes: "email name",
    state: nonce.slice(0, 16),
    nonce,
  });

  const r = result?.response;
  if (!r?.identityToken) throw new Error("Token Apple mancante");

  return {
    idToken: r.identityToken,
    authorizationCode: r.authorizationCode,
    email: r.email || null,
    fullName: [r.givenName, r.familyName].filter(Boolean).join(" ") || null,
    user: r.user, // stable Apple user ID
    nonce,
  };
}

/* ─────────────────────────────────────────────────────────────────────
 * FILESYSTEM — salva ZIP pacchetto annuncio nei file dell'app
 * Doc: https://capacitorjs.com/docs/apis/filesystem
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeSaveFile({ path, data, encoding = "base64" } = {}) {
  if (!isNative) return null;
  const { Filesystem, Directory, Encoding } = await import("@capacitor/filesystem");
  await Filesystem.writeFile({
    path,
    data,
    directory: Directory.Data,
    encoding: encoding === "utf8" ? Encoding.UTF8 : undefined,
    recursive: true,
  });
  const uri = await Filesystem.getUri({ path, directory: Directory.Data });
  return uri.uri;
}

/* ─────────────────────────────────────────────────────────────────────
 * SHARE — invoca il share sheet nativo iOS (anteprima link, WhatsApp, ecc.)
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeShare({ title, text, url, files = [] }) {
  if (!isNative) {
    // Fallback web: usa Web Share API se disponibile
    if (navigator.share) {
      try { await navigator.share({ title, text, url, files }); return true; }
      catch (_) { return false; }
    }
    return false;
  }
  const { Share } = await import("@capacitor/share");
  try { await Share.share({ title, text, url, files }); return true; }
  catch (_) { return false; }
}

/* ─────────────────────────────────────────────────────────────────────
 * HAPTICS — micro-vibrazione tattile sui tap critici
 * iter v29 r168 — "premium iPhone feel" senza disturbare web/Android
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeHaptic(style = "medium") {
  if (!isNative) {
    // Fallback web: Web Vibration API (Android Chrome la supporta)
    try { navigator.vibrate?.(style === "heavy" ? 25 : 12); } catch (_) { /* silent */ }
    return;
  }
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const map = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: map[style] || ImpactStyle.Medium });
  } catch (_) { /* plugin non installato */ }
}

/**
 * Haptic semantico (success / warning / error) — usa il Taptic Engine
 * `notification` di Apple, che produce pattern distinguibili a occhi chiusi
 * (utile mentre l'agente guida o tiene il telefono in tasca).
 *
 * iter v31 r10 — Feedback "ho capito quello che hai detto" per il flow
 * voce → estrazione agenda. Quando l'estrattore riconosce contatti o
 * azioni concrete: success ("tap-tap" alto). Quando il memo è vago:
 * warning. Quando c'è errore tecnico: error.
 */
export async function nativeHapticNotification(type = "success") {
  if (!isNative) {
    // Web fallback: pattern di vibrazione che approssima il feedback
    try {
      if (type === "success") navigator.vibrate?.([15, 50, 15]);
      else if (type === "warning") navigator.vibrate?.([30, 80, 30]);
      else navigator.vibrate?.([60, 40, 60]);
    } catch (_) { /* silent */ }
    return;
  }
  try {
    const { Haptics, NotificationType } = await import("@capacitor/haptics");
    const map = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error,
    };
    await Haptics.notification({ type: map[type] || NotificationType.Success });
  } catch (_) { /* plugin non installato */ }
}

/* ─────────────────────────────────────────────────────────────────────
 * PUSH — unregister del token corrente al logout
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeUnregisterPush() {
  if (!isNative) return null;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    // Capacitor 7 espone unregister() che rimuove TUTTI i listener e
    // dice al sistema iOS che non vogliamo più push.
    await PushNotifications.unregister();
    return true;
  } catch (_) { return false; }
}


/* ─────────────────────────────────────────────────────────────────────
 * OAUTH BROWSER + DEEP LINK — Emergent Google Auth su iOS native.
 * iter v29 r191 — Apre auth.emergentagent.com in SFSafariViewController
 * (non WebView, Google non blocca), ascolta il ritorno via Universal Link
 * https://prontoimmo.it/dashboard#session_id=XXX e chiude la Safari View.
 * ───────────────────────────────────────────────────────────────────── */
export async function nativeOpenOAuth(url) {
  if (!isNative) {
    // Su web: redirect classico in-page (comportamento Emergent Auth)
    window.location.href = url;
    return;
  }
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({
      url,
      // iOS: usa SFSafariViewController (modale, con back button "Done")
      presentationStyle: "fullscreen",
      // Tinta che richiama il brand sage
      toolbarColor: "#7C8B6E",
    });
  } catch (e) {
    // Se il plugin Browser non è disponibile, fallback al redirect classico
    window.location.href = url;
  }
}

export async function nativeCloseBrowser() {
  if (!isNative) return;
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch (_) { /* già chiuso */ }
}

/**
 * Sottoscrive il listener per i deep link in arrivo (Universal Links).
 * @param {(url: string) => void} handler — chiamato ogni volta che iOS
 *        apre l'app via https://prontoimmo.it/...  (es. dopo OAuth Google).
 * @returns {() => void} unsubscribe function — chiamala al cleanup.
 */
export async function nativeOnDeepLink(handler) {
  if (!isNative) return () => {};
  try {
    const { App } = await import("@capacitor/app");
    const sub = await App.addListener("appUrlOpen", (event) => {
      try { handler(event.url); } catch (_) { /* swallow */ }
    });
    return () => { try { sub.remove(); } catch (_) { /* silent */ } };
  } catch (_) {
    return () => {};
  }
}


/* ─────────────────────────────────────────────────────────────────────
 * GOOGLE SIGN IN NATIVO (iOS) — @capgo/capacitor-social-login
 * iter v29 r200 — sostituisce il flow Emergent (auth.emergentagent.com
 * via Safari View) con il foglio nativo Google iOS:
 *   1. L'utente preme "Continua con Google" nell'app
 *   2. iOS mostra il selettore account Google nativo (zero domini esterni)
 *   3. Il plugin ritorna un idToken Google firmato
 *   4. Lo mandiamo al backend → /api/auth/google-native → JWT Pronto
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 * ───────────────────────────────────────────────────────────────────── */
let _socialLoginInitialized = false;

async function _ensureSocialLoginInit() {
  if (!isNative) return false;
  if (_socialLoginInitialized) return true;
  const iosClientId = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID;
  if (!iosClientId) {
    remoteLog("ios-google-login", "init.error.no-ios-client-id");
    throw new Error("Configurazione Google iOS Client ID mancante");
  }
  remoteLog("ios-google-login", "init.start", { iosClientId });
  try {
    const { SocialLogin } = await import("@capgo/capacitor-social-login");
    await SocialLogin.initialize({
      google: {
        iOSClientId: iosClientId,
        // Mode "online" → ritorna idToken (quello che verifica il backend).
        // Niente accessToken serverAuthCode necessari per il nostro caso.
        mode: "online",
      },
    });
    _socialLoginInitialized = true;
    remoteLog("ios-google-login", "init.ok");
    return true;
  } catch (e) {
    remoteLog("ios-google-login", "init.exception", {
      message: String(e?.message || e),
      code: e?.code,
      stack: e?.stack,
    });
    throw new Error(`Init plugin Google fallita: ${e?.message || e}`);
  }
}

/**
 * Avvia il foglio nativo Google e ritorna l'idToken Google.
 * @returns {Promise<{ idToken: string, email?: string, name?: string } | null>}
 */
export async function nativeGoogleSignIn() {
  if (!isNative) return null;
  remoteLog("ios-google-login", "signin.start");
  await _ensureSocialLoginInit();
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  let res;
  try {
    res = await SocialLogin.login({
      provider: "google",
      options: {
        scopes: ["email", "profile"],
      },
    });
  } catch (e) {
    remoteLog("ios-google-login", "signin.login-exception", {
      message: String(e?.message || e),
      code: e?.code,
      stack: e?.stack,
    });
    throw e;
  }
  remoteLog("ios-google-login", "signin.login-returned", {
    hasResult: !!res,
    keys: res ? Object.keys(res) : [],
    resultKeys: res?.result ? Object.keys(res.result) : [],
  });
  // La forma esatta del result dipende dalla versione del plugin.
  // Capgo v7+: { provider: 'google', result: { responseType: 'online', idToken, accessToken, profile: { email, name, ... } } }
  const r = res?.result || res || {};
  const idToken = r.idToken || r.id_token || r.token;
  const profile = r.profile || {};
  if (!idToken) {
    remoteLog("ios-google-login", "signin.no-idtoken", { fullResult: res });
    console.error("[native] Google sign-in: idToken mancante", res);
    return null;
  }
  remoteLog("ios-google-login", "signin.ok", {
    hasIdToken: true,
    email: profile.email,
  });
  return {
    idToken,
    email: profile.email,
    name: profile.name || profile.displayName,
    picture: profile.imageUrl || profile.picture,
  };
}

/**
 * Logout dal provider sociale (revoca consenso lato Google sull'app).
 * Tipicamente chiamato insieme al logout interno Pronto.
 */
export async function nativeGoogleSignOut() {
  if (!isNative) return;
  try {
    const { SocialLogin } = await import("@capgo/capacitor-social-login");
    await SocialLogin.logout({ provider: "google" });
  } catch (_) { /* silent */ }
}
